import { useCallback, useEffect, useMemo, useState } from "react";
import { KeyRound, Loader2, Info } from "lucide-react";
import api from "../../api/api";
import { Alert, EmptyState, Panel, Pill, Stat, apiError, formatDate, formatMonth, useOrgStyles } from "../../components/org/orgUi";
import { useOrgPortal } from "./orgPortalContext";

/**
 * Monthly seats. A license is valid from the 1st to the end of the month and
 * renews automatically. A removed license's seat stays used until the month
 * ends, so it can't be handed to someone else until the 1st.
 */
export default function OrgLicenses() {
    const { orgId, reloadOrg } = useOrgPortal();
    const s = useOrgStyles();
    const [summary, setSummary] = useState(null);
    const [staff, setStaff] = useState([]);
    const [roster, setRoster] = useState([]);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [coachPick, setCoachPick] = useState("");
    const [studentPick, setStudentPick] = useState("");

    const load = useCallback(async () => {
        try {
            const [lic, st, ro] = await Promise.all([
                api.get(`/org/${orgId}/licenses`),
                api.get(`/org/${orgId}/staff`),
                api.get(`/org/${orgId}/roster?sort=alpha`),
            ]);
            setSummary(lic.data);
            setStaff(st.data.staff || []);
            setRoster(ro.data.students || []);
        } catch (err) {
            setError(apiError(err, "Couldn't load licenses."));
        }
    }, [orgId]);

    useEffect(() => { load(); }, [load]);

    const licensedCoaches = useMemo(() => new Set((summary?.licenses || []).filter((l) => l.LicenseType === "coach" && l.IsActive).map((l) => l.AdminID)), [summary]);
    const licensedStudents = useMemo(() => new Set((summary?.licenses || []).filter((l) => l.LicenseType === "student" && l.IsActive).map((l) => l.ClientID)), [summary]);
    // Someone whose license was removed earlier this month can get it back
    // without a new seat (the backend reuses their seat).
    const reusable = useMemo(() => new Set((summary?.licenses || []).filter((l) => !l.IsActive)
        .map((l) => `${l.LicenseType}:${l.AdminID || l.ClientID}`)), [summary]);
    const coachOptions = staff.filter((m) => m.IsCoach && !licensedCoaches.has(m.AdminID));
    const studentOptions = roster.filter((m) => !licensedStudents.has(m.ClientID));

    const act = async (fn) => {
        setBusy(true);
        setError("");
        try {
            await fn();
            await Promise.all([load(), reloadOrg()]);
        } catch (err) {
            setError(apiError(err));
        } finally {
            setBusy(false);
        }
    };

    const assign = (type, id) => act(async () => {
        await api.post(`/org/${orgId}/licenses`, type === "coach" ? { licenseType: "coach", adminId: Number(id) } : { licenseType: "student", clientId: Number(id) });
        if (type === "coach") setCoachPick(""); else setStudentPick("");
    });

    const revoke = (lic) => {
        const ok = window.confirm(
            `Remove ${lic.FirstName} ${lic.LastName}'s license?\n\nThey lose access now. The seat stays used until ${formatMonth(summary.nextPeriod)} begins, so it can't go to someone else this month.`
        );
        if (ok) act(() => api.delete(`/org/${orgId}/licenses/${lic.LicenseID}`));
    };

    if (!summary) {
        return error ? <Alert>{error}</Alert> : <div className="flex justify-center py-10"><Loader2 className={`animate-spin ${s.muted}`} /></div>;
    }

    const pools = [
        { key: "coach", title: "Coach licenses", pool: summary.coach, options: coachOptions, pick: coachPick, setPick: setCoachPick,
          optionLabel: (m) => `${m.FirstName} ${m.LastName}`, optionValue: (m) => m.AdminID,
          emptyHint: "Add coaches under Coaches & Admins first." },
        { key: "student", title: "Student licenses", pool: summary.student, options: studentOptions, pick: studentPick, setPick: setStudentPick,
          optionLabel: (m) => `${m.LastName}, ${m.FirstName} (${m.EmailAddress})`, optionValue: (m) => m.ClientID,
          emptyHint: "Every student on your roster already has a license." },
    ];

    return (
        <div className="space-y-6">
            <Alert onClose={() => setError("")}>{error}</Alert>
            <div className={`flex items-start gap-3 rounded-2xl border p-4 text-sm ${s.subPanel}`}>
                <Info size={18} className={`mt-0.5 flex-shrink-0 ${s.muted}`} />
                <p className={s.body}>
                    <strong className={s.text}>{formatMonth(summary.period)}</strong>: licenses run through {formatDate(summary.periodEnd)} and renew automatically.
                    A license you remove can't be given to someone else until {formatDate(summary.nextPeriod)}.
                </p>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
                {pools.map(({ key, title, pool, options, pick, setPick, optionLabel, optionValue, emptyHint }) => (
                    <Panel key={key} title={title}>
                        <div className="grid grid-cols-3 gap-3">
                            <Stat label="Used" value={`${pool.used} / ${pool.limit}`} tone={pool.limit > 0 && pool.available === 0 ? "warn" : "default"} />
                            <Stat label="Available" value={pool.available} />
                            <Stat label="Next month" value={pool.availableNextMonth} hint="available" />
                        </div>
                        {pool.limit === 0 ? (
                            <p className={`mt-4 text-sm ${s.muted}`}>Your plan doesn't include {key} licenses. Contact Build My Biz Brand to add them.</p>
                        ) : (
                            <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                                <select aria-label={`Assign a ${key} license`} className={s.input} value={pick} onChange={(e) => setPick(e.target.value)}
                                        disabled={options.length === 0}>
                                    <option value="">
                                        {options.length === 0 ? emptyHint : pool.available === 0 ? "No new licenses left this month" : `Choose a ${key}…`}
                                    </option>
                                    {options.map((m) => {
                                        const canReuse = reusable.has(`${key}:${optionValue(m)}`);
                                        return (
                                            <option key={optionValue(m)} value={optionValue(m)} disabled={pool.available === 0 && !canReuse}>
                                                {optionLabel(m)}{canReuse ? " (restore this month's license)" : ""}
                                            </option>
                                        );
                                    })}
                                </select>
                                <button type="button" className={s.btnPrimary}
                                        disabled={!pick || busy || (pool.available === 0 && !reusable.has(`${key}:${pick}`))}
                                        onClick={() => assign(key, pick)}>
                                    Assign
                                </button>
                            </div>
                        )}
                    </Panel>
                ))}
            </div>

            <Panel title={`Licenses this month (${summary.licenses.length})`} padded={false}>
                {summary.licenses.length === 0 ? (
                    <EmptyState icon={KeyRound} title="No licenses assigned this month" body="Assign coach and student licenses above." />
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${s.dark ? "border-slate-700" : "border-slate-100"}`}>
                                    <th className={s.th}>Name</th>
                                    <th className={s.th}>Type</th>
                                    <th className={s.th}>Assigned</th>
                                    <th className={s.th}>Status</th>
                                    <th className={s.th}><span className="sr-only">Actions</span></th>
                                </tr>
                            </thead>
                            <tbody>
                                {summary.licenses.map((l) => (
                                    <tr key={l.LicenseID} className={`border-b last:border-0 ${s.row}`}>
                                        <td className={s.td}>
                                            <div className={`font-semibold ${s.text}`}>{l.FirstName} {l.LastName}</div>
                                            <div className={`text-xs ${s.muted}`}>{l.Email}</div>
                                        </td>
                                        <td className={s.td}><Pill tone={l.LicenseType === "coach" ? "primary" : "neutral"}>{l.LicenseType === "coach" ? "Coach" : "Student"}</Pill></td>
                                        <td className={`${s.td} whitespace-nowrap`}>{formatDate(l.AssignedOn)}</td>
                                        <td className={s.td}>
                                            {l.IsActive
                                                ? <Pill tone="success">Active</Pill>
                                                : <Pill tone="warning">Removed · seat frees {formatDate(summary.nextPeriod)}</Pill>}
                                        </td>
                                        <td className={`${s.td} text-right`}>
                                            {l.IsActive && <button type="button" className={s.btnDanger} disabled={busy} onClick={() => revoke(l)}>Remove</button>}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
        </div>
    );
}
